import { createFileRoute } from "@tanstack/react-router";
import { BreadcrumbHeader } from "@/components/primitives/breadcrumb-header";
import {
  Article,
  ArticleAside,
  ArticleBody,
  ArticleContainer,
  ArticleDescription,
  ArticleHeader,
  ArticleHeading,
  ArticleHighlightImage,
  ArticleList,
  ArticleListItem,
  ArticleOrderedList,
  ArticleOrderedListItem,
  ArticleParagraph,
  ArticleRelatedLink,
  ArticleSection,
  ArticleSectionHeader,
  ArticleSectionSubHeader,
} from "@/components/primitives/article";
import { PageContainer } from "@/components/primitives/layout";
import { SingleBreadcrumb } from "@/components/primitives/single-breadcrumb";
import { UnderlineLink } from "@/components/primitives/underline-link";
import { LollipopIcon, SnowflakeIcon } from "lucide-react";

const TITLE = "How to fix hard ice cream";
const DESCRIPTION =
  "Learn how to adjust your ice cream recipe to make it softer and easier to scoop.";
const IMAGE_URL = "/articles/too-hard.webp";

function IngredientMetrics({
  fpdf,
  sweetness,
}: {
  fpdf: string;
  sweetness: string;
}) {
  return (
    <span className="text-muted-foreground mt-1 block flex items-center gap-2 text-sm leading-5 font-normal">
      <span className="flex items-center gap-1">
        <SnowflakeIcon className="size-4 text-blue-500" />{" "}
        <span>FPDF {fpdf}</span>
      </span>
      <span className="flex items-center gap-1">
        <LollipopIcon className="size-4 text-pink-500" />
        <span>Sweetness {sweetness}</span>
      </span>
    </span>
  );
}

export const Route = createFileRoute("/learn/how-to-fix-hard-ice-cream")({
  head: () => ({
    meta: [
      { title: `${TITLE} - Scoopulator` },
      { name: "description", content: DESCRIPTION },
    ],
  }),
  component: FixHardIceCreamPage,
});

function FixHardIceCreamPage() {
  return (
    <PageContainer>
      <ArticleContainer>
        <BreadcrumbHeader>
          <SingleBreadcrumb href="/learn" label="Learn more about ice cream" />
        </BreadcrumbHeader>

        <Article>
          <ArticleHeader>
            <ArticleHeading>{TITLE}</ArticleHeading>
            <ArticleDescription>{DESCRIPTION}</ArticleDescription>
          </ArticleHeader>
          <ArticleHighlightImage src={IMAGE_URL} alt={TITLE} />

          <ArticleBody>
            <ArticleSection>
              <ArticleParagraph>
                A common problem with homemade ice cream (and even some
                commercial brands) is that it freezes too hard and can be
                impossible to scoop.
              </ArticleParagraph>
              <ArticleParagraph>
                One solution, of course, is to let it warm up on the counter
                before serving. But there is another, much more satisfying
                solution: adjust the recipe so that your ice cream is perfectly
                scoopable, straight out of the freezer.
              </ArticleParagraph>
            </ArticleSection>

            <ArticleAside label="Some simple steps, if you don't want to read the whole article">
              <ArticleOrderedList className="text-md">
                <ArticleOrderedListItem>
                  <strong>Choose your serving temperature</strong> Check your
                  freezer temperature and add 2 degrees to it. This is your
                  serving temperature, which you can select in the calculator.
                </ArticleOrderedListItem>
                <ArticleOrderedListItem>
                  <strong>Create a recipe in the calculator</strong> and adjust
                  ingredient quantities to reach your desired firmness. You may
                  need to use some new ingredients (such as skim milk powder or
                  dextrose) to help hit that target.
                </ArticleOrderedListItem>
                <ArticleOrderedListItem>
                  <strong>Churn your ice cream</strong> and see how well it
                  scoops at your chosen serving temperature. If it's still too
                  hard, adjust the recipe toward a softer firmness. If it's too
                  soft, aim for a firmer one.
                </ArticleOrderedListItem>
              </ArticleOrderedList>
            </ArticleAside>

            <ArticleSection>
              <ArticleSectionHeader id="choose-serving-temperature">
                Choosing your serving temperature and firmness
              </ArticleSectionHeader>
              <ArticleParagraph>
                <strong>Serving temperature</strong> is the temperature at which
                you plan to scoop and eat your ice cream.{" "}
                <strong>Firmness</strong> describes how hard or soft it is at
                that temperature.
              </ArticleParagraph>
              <ArticleParagraph>
                Most typical home freezers are set to around -18°C (0°F), but
                you should check your own settings (or if it's old, verify with
                a thermometer) to be sure. Once you know your freezer
                temperature, that value should be the <em>lower limit</em> of
                your serving temperature.
              </ArticleParagraph>
              <ArticleParagraph>
                While you could choose the same temperature as your freezer,
                your ice cream will warm up a little as you scoop and serve it.
                Additionally, home freezers will go through defrost cyles
                periodically, causing temporary temperature fluctuations. A good
                rule of thumb is to add ~2 degrees to your freezer temperature
                to give yourself some wiggle room.
              </ArticleParagraph>
              <ArticleParagraph>
                For the typical home freezer temperature of -18°C (0°F), a good
                starting point is around -16°C (3°F). Once you make your recipe
                and taste it, you can (and should!) adjust your firmness target
                to get the texture you prefer.
              </ArticleParagraph>
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="hit-serving-temperature">
                Dialing in your recipe to reach your desired firmness
              </ArticleSectionHeader>
              <ArticleParagraph>
                Now that you have your serving temperature, you can use the{" "}
                <UnderlineLink href="/calc">ice cream calculator</UnderlineLink>{" "}
                to build your recipe. The calculator will show you the estimated
                firmness at your chosen temperature, based on the ingredients
                and their proportions. Adjust those ingredients to bring the
                recipe closer to your firmness target.
              </ArticleParagraph>
              <ArticleParagraph>
                To soften an ice cream recipe, you usually want to:
              </ArticleParagraph>
              <ArticleList>
                <ArticleListItem>
                  <strong>
                    Add ingredients with strong freezing point depression
                  </strong>{" "}
                  (like sugar, alcohol, or salt)
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Minimize the amount of water</strong> (also known as
                  increasing total solids)
                </ArticleListItem>
              </ArticleList>
              <ArticleParagraph>
                While doing so, you need to keep an eye on the three most
                important metrics: <strong>firmness</strong>,{" "}
                <strong>sweetness</strong>, and <strong>total solids</strong>.
                Sometimes, it isn't possible to hit all three targets at once
                without reaching for a new ingredient.
              </ArticleParagraph>
              <ArticleAside label="A brief primer on freezing point depression">
                <p>
                  Water wants to freeze at 0°C (32°F). But when you dissolve
                  other ingredients in it, the temperature at which it freezes
                  goes down. Different ingredients have different effects on the
                  freezing point, and the more you add, the lower the freezing
                  point goes. This is called freezing point depression.
                </p>
              </ArticleAside>
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="lower-the-freezing-point">
                Ingredients that lower the freezing point
              </ArticleSectionHeader>
              <ArticleParagraph>
                There is no single best ingredient for softening ice cream. Each
                one has different effects on freezing point, sweetness, and
                total solids, so most balanced recipes use a combination to
                achieve the desired texture and sweetness.
              </ArticleParagraph>
              <ArticleAside label="How these ingredients are compared">
                <p>
                  Freezing point depression and sweetness are measured relative
                  to granulated sugar (sucrose), the most common texture and
                  sweetness modifier in ice cream. The two metrics are:
                </p>
                <ArticleList className="text-md">
                  <ArticleListItem>
                    <strong>FPDF</strong> (Freezing Point Depression Factor) is
                    a measure of how much an ingredient lowers the freezing
                    point of water. A higher FPDF means more anti-freezing power
                    by weight.
                  </ArticleListItem>
                  <ArticleListItem>
                    <strong>Sweetness</strong> is a measure of how sweet an
                    ingredient tastes compared to granulated sugar.
                  </ArticleListItem>
                </ArticleList>
              </ArticleAside>

              <ArticleSectionSubHeader>
                <UnderlineLink
                  to="/ingredients/$slug"
                  params={{ slug: "usdaff-746784" }}
                >
                  Granulated sugar
                </UnderlineLink>
                <IngredientMetrics fpdf="100%" sweetness="100%" />
              </ArticleSectionSubHeader>
              <ArticleParagraph>
                Granulated sugar, or sucrose, is the standard starting point. It
                adds sweetness and solids while lowering the freezing point.
                Adding more will soften a hard recipe, but it can quickly make
                the ice cream too sweet. For that reason, sucrose is often used
                alongside a less-sweet ingredient with stronger anti-freezing
                power.
              </ArticleParagraph>

              <ArticleSectionSubHeader>
                <UnderlineLink
                  to="/ingredients/$slug"
                  params={{ slug: "usdafndds-2705585" }}
                >
                  Skim milk powder
                </UnderlineLink>
                <IngredientMetrics fpdf="67%" sweetness="8%" />
              </ArticleSectionSubHeader>
              <ArticleParagraph>
                Skim milk powder (or nonfat dry milk) is a fantastic ingredient
                for softening ice cream because lactose has very little
                sweetness. It allows you to add more dairy flavor and solids
                without introducing more water or fat.
              </ArticleParagraph>

              <ArticleSectionSubHeader>
                <UnderlineLink
                  to="/ingredients/$slug"
                  params={{ slug: "dextrose" }}
                >
                  Dextrose
                </UnderlineLink>
                <IngredientMetrics fpdf="190%" sweetness="70%" />
              </ArticleSectionSubHeader>
              <ArticleParagraph>
                Dextrose is one of the most useful tools in an ice cream pantry.
                It has nearly twice the freezing point depression of sucrose but
                is only about 70% as sweet. It's common to replace up to 50% of
                the sucrose in a recipe with dextrose to soften it dramatically.
              </ArticleParagraph>

              <ArticleSectionSubHeader>
                <UnderlineLink
                  to="/ingredients/$slug"
                  params={{ slug: "usdafndds-2710704" }}
                >
                  Vodka and other alcohols
                </UnderlineLink>
                <IngredientMetrics fpdf="247%" sweetness="0%" />
              </ArticleSectionSubHeader>
              <ArticleParagraph>
                Alcohol is one of the strongest common freezing point
                depressants. A small amount of vodka can dramatically soften ice
                cream without adding any sweetness or other flavor.
              </ArticleParagraph>

              <ArticleSectionSubHeader>
                <UnderlineLink
                  to="/ingredients/$slug"
                  params={{ slug: "usdaff-746775" }}
                >
                  Salt
                </UnderlineLink>
                <IngredientMetrics fpdf="1,111%" sweetness="0%" />
              </ArticleSectionSubHeader>
              <ArticleParagraph>
                Salt has very strong anti-freezing power, but flavor limits how
                much you can use. It is recommended to add a small amount to any
                recipe to enhance the other flavors.
              </ArticleParagraph>

              <ArticleSectionSubHeader>
                <UnderlineLink
                  to="/ingredients/$slug"
                  params={{ slug: "allulose" }}
                >
                  Allulose
                </UnderlineLink>
                <IngredientMetrics fpdf="190%" sweetness="70%" />
              </ArticleSectionSubHeader>
              <ArticleParagraph>
                Allulose is one of the most popular sugar substitutes in ice
                cream, because it behaves remarkably like dextrose while
                contributing only a tenth of the calories. Large amounts (20g
                for a typical adult) may cause{" "}
                <UnderlineLink
                  href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6315886/"
                  target="_blank"
                  rel="noopener noreferrer"
                  external
                >
                  digestive discomfort
                </UnderlineLink>
                , so it is best used in moderation.
              </ArticleParagraph>

              <ArticleSectionSubHeader>
                <UnderlineLink
                  to="/ingredients/$slug"
                  params={{ slug: "glycerin" }}
                >
                  Glycerin
                </UnderlineLink>
                <IngredientMetrics fpdf="370%" sweetness="60%" />
              </ArticleSectionSubHeader>
              <ArticleParagraph>
                Food grade glycerin (also known as vegetable glycerine or
                glycerol) is a sugar alcohol with very strong freezing point
                depression and mild sweetness. It can have a laxative effect in
                larger amounts (
                <UnderlineLink
                  href="https://pmc.ncbi.nlm.nih.gov/articles/PMC7009851/"
                  target="_blank"
                  rel="noopener noreferrer"
                  external
                >
                  10g for a typical adult
                </UnderlineLink>
                ) so it is also best used in moderation.
              </ArticleParagraph>

              <ArticleSectionSubHeader>
                <UnderlineLink
                  to="/ingredients/$slug"
                  params={{ slug: "inulin" }}
                >
                  Inulin
                </UnderlineLink>
                <IngredientMetrics fpdf="65%" sweetness="10%" />
              </ArticleSectionSubHeader>
              <ArticleParagraph>
                Inulin is a soluble fiber that adds solids and body with very
                little sweetness. It is a popular option for adding body and
                creaminess low-fat recipes such as sorbets.
              </ArticleParagraph>
            </ArticleSection>
            <ArticleRelatedLink
              className="mt-8"
              href="/calc"
              title="Balance your recipe with the Scoopulator ice cream calculator"
              label="Try it out"
              srLabel="Ice cream calculator"
              description="Design a recipe that's perfectly scoopable, straight out of the freezer."
            />
          </ArticleBody>
        </Article>
      </ArticleContainer>
    </PageContainer>
  );
}
